import React, { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdditionalActivitiesManagement } from '../ui/diary/AdditionalActivitiesManagement';
import type {
  AdditionalActivitiesManagementData,
  AdditionalActivitiesManagementLabels,
} from '../shared/types/additional-activities';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

export interface AdditionalActivitiesManagementContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  labels: AdditionalActivitiesManagementLabels;
}

const GET_MANAGEMENT_QUERY = `
  query GetAdditionalActivitiesManagement($sortBy: String!, $sortDirection: String!) {
    additionalActivitiesManagement(sortBy: $sortBy, sortDirection: $sortDirection) {
      activities {
        name
        usageCount
        firstOccurrence
        lastOccurrence
      }
      backUrl
      convertToToggleFieldBaseUrl
    }
  }
`;

const RENAME_ACTIVITY_MUTATION = `
  mutation RenameAdditionalActivity($oldName: String!, $newName: String!) {
    renameAdditionalActivity(input: { oldName: $oldName, newName: $newName }) {
      success
      updatedCount
    }
  }
`;

const MERGE_ACTIVITIES_MUTATION = `
  mutation MergeAdditionalActivities($sourceNames: [String!]!, $targetName: String!) {
    mergeAdditionalActivities(input: { sourceNames: $sourceNames, targetName: $targetName }) {
      success
      mergedCount
    }
  }
`;

const BULK_REMOVE_MUTATION = `
  mutation BulkRemoveAdditionalActivities($names: [String!]!) {
    bulkRemoveAdditionalActivities(input: { names: $names }) {
      success
      removedCount
    }
  }
`;

function AdditionalActivitiesManagementInner({
  graphqlEndpoint,
  csrfToken,
  labels,
}: AdditionalActivitiesManagementContainerProps) {
  const qc = useQueryClient();
  const [sortBy, setSortBy] = useState('last_used');
  const [sortDirection, setSortDirection] = useState('desc');

  const { data, isLoading } = useQuery({
    queryKey: ['additionalActivitiesManagement', sortBy, sortDirection],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GET_MANAGEMENT_QUERY,
          variables: { sortBy, sortDirection },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.additionalActivitiesManagement as AdditionalActivitiesManagementData;
    },
  });

  const renameMutation = useMutation({
    mutationFn: async ({ oldName, newName }: { oldName: string; newName: string }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: RENAME_ACTIVITY_MUTATION,
          variables: { oldName, newName },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to rename activity');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.renameAdditionalActivity;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additionalActivitiesManagement'] });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: async ({ sourceNames, targetName }: { sourceNames: string[]; targetName: string }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: MERGE_ACTIVITIES_MUTATION,
          variables: { sourceNames, targetName },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to merge activities');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.mergeAdditionalActivities;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additionalActivitiesManagement'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (names: string[]) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: BULK_REMOVE_MUTATION,
          variables: { names },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete activities');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.bulkRemoveAdditionalActivities;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additionalActivitiesManagement'] });
    },
  });

  const handleSort = useCallback((field: string) => {
    setSortBy((currentSortBy) => {
      if (currentSortBy === field) {
        setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return field;
      } else {
        setSortDirection('asc');
        return field;
      }
    });
  }, []);

  const handleRename = useCallback(
    (oldName: string, newName: string) => {
      return renameMutation.mutateAsync({ oldName, newName });
    },
    [renameMutation]
  );

  const handleMerge = useCallback(
    (sourceNames: string[], targetName: string) => {
      return mergeMutation.mutateAsync({ sourceNames, targetName });
    },
    [mergeMutation]
  );

  const handleDelete = useCallback(
    (names: string[]) => {
      return deleteMutation.mutateAsync(names);
    },
    [deleteMutation]
  );

  if (isLoading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <AdditionalActivitiesManagement
      activities={data.activities}
      backUrl={data.backUrl}
      convertToToggleFieldBaseUrl={data.convertToToggleFieldBaseUrl}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={handleSort}
      onRename={handleRename}
      onMerge={handleMerge}
      onDelete={handleDelete}
      labels={labels}
    />
  );
}

export function AdditionalActivitiesManagementContainer(props: AdditionalActivitiesManagementContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AdditionalActivitiesManagementInner {...props} />
    </QueryClientProvider>
  );
}

export function mountAdditionalActivitiesManagement(
  container: HTMLElement,
  props: AdditionalActivitiesManagementContainerProps
) {
  const existingRoot = (container as any).__reactRoot;
  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = createRoot(container);
  (container as any).__reactRoot = root;
  root.render(<AdditionalActivitiesManagementContainer {...props} />);
  return root;
}

if (typeof window !== 'undefined') {
  (window as any).mountAdditionalActivitiesManagement = mountAdditionalActivitiesManagement;
}

export default AdditionalActivitiesManagementContainer;
